import React from "react";
import { Link } from "react-router-dom";
import { Mail, Linkedin, Twitter, Facebook } from "lucide-react";
import Logo from "@/components/Logo";

const PRODUCER_CAPACITY_SHORT =
  "Lifespan Wealth Partners is a licensed insurance producer. We are not a registered investment adviser. Nothing on this site is investment, tax, or legal advice.";

const DISCLAIMER =
  "Lifespan Wealth Partners provides educational information regarding life insurance, annuities, health insurance, and long-term care products. The information presented on this website is for educational purposes only and is not intended as tax, legal, investment, or financial advice. Insurance and annuity products are offered through licensed insurance professionals and are subject to underwriting approval, product availability, and applicable state regulations. Guarantees are backed by the financial strength and claims-paying ability of the issuing insurance carrier. Please consult a qualified professional regarding your specific situation. © 2026 Lifespan Wealth Partners. All rights reserved.";

const COLS = [
  {
    title: "Solutions",
    links: [
      { label: "Term Life", href: "#solutions" },
      { label: "Annuities", href: "#solutions" },
      { label: "Index Universal Life", href: "#solutions" },
      { label: "Long-Term Care", href: "#solutions" },
      { label: "Medicare (Coming Soon)", href: "/products/medicare" },
    ],
  },
  {
    title: "Firm",
    links: [
      { label: "Our Vision", href: "#vision" },
      { label: "Advisors", href: "#advisors" },
      { label: "Process", href: "/process" },
      { label: "Download Firm Overview (PDF)", href: "/api/pdf/about", external: true, testId: "footer-firm-overview-pdf" },
      { label: "Carrier Network", href: "/carriers", route: true, testId: "footer-carriers-link" },
    ],
  },
  {
    title: "Compliance",
    links: [
      { label: "Compliance & Disclosures", href: "/compliance", route: true, testId: "footer-compliance-link" },
      { label: "License Information", href: "/compliance#licenses", route: true },
      { label: "IUL Tax Treatment", href: "/compliance#iul-tax", route: true },
      { label: "Medicare TPMO", href: "/compliance#medicare-tpmo", route: true },
    ],
  },
  {
    title: "Contact",
    links: [
      {
        label: "info@lifespanwealthpartners.com",
        href: "mailto:info@lifespanwealthpartners.com",
      },
      { label: "Book a conversation", href: "#contact" },
    ],
  },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#091E38] text-[#FAF8F3] pt-16 pb-10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-4">
            <Logo variant="light" />
            <p className="mt-5 text-sm text-[#FAF8F3]/70 leading-relaxed max-w-sm">
              Financial vision for every stage of life — from first steps to lasting legacy.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[Mail, Linkedin, Twitter, Facebook].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="Social"
                  data-testid={`footer-social-${i}`}
                  className="w-9 h-9 rounded-full border border-[#FAF8F3]/15 flex items-center justify-center text-[#FAF8F3]/80 hover:border-lw-gold hover:text-lw-gold transition-colors"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.title} className="lg:col-span-2">
              <h4 className="font-sans-ui text-[0.7rem] tracking-[0.2em] uppercase text-lw-gold">
                {c.title}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {c.links.map((l) =>
                  l.route ? (
                    <li key={l.label}>
                      <Link
                        to={l.href}
                        data-testid={l.testId}
                        className="text-sm text-[#FAF8F3]/75 hover:text-[#FAF8F3] transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ) : (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        target={l.external ? "_blank" : undefined}
                        rel={l.external ? "noopener noreferrer" : undefined}
                        data-testid={l.testId}
                        className="text-sm text-[#FAF8F3]/75 hover:text-[#FAF8F3] transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="gold-hairline opacity-60 mt-14 mb-6" />

        {/* Producer capacity strip — sits above the main educational disclaimer */}
        <p
          data-testid="footer-producer-capacity"
          className="text-xs text-[#FAF8F3]/70 leading-relaxed max-w-5xl mb-5"
        >
          {PRODUCER_CAPACITY_SHORT} See our{" "}
          <Link
            to="/compliance"
            data-testid="footer-producer-capacity-compliance-link"
            className="text-lw-gold hover:underline"
          >
            Compliance &amp; Disclosures
          </Link>{" "}
          page for full regulatory information.
        </p>

        <p
          data-testid="footer-disclaimer"
          className="text-xs text-[#FAF8F3]/55 leading-relaxed max-w-5xl"
        >
          {DISCLAIMER}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
